import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import type { TripItineraryItem, TripItemWithoutDay } from 'tripPlanner/api/types'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'

interface Props {
  item: TripItineraryItem | TripItemWithoutDay
  disabled?: boolean
}

export default function ActivityLocationAction({ item, disabled }: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending, isManualItineraryMovePending } = useItineraryContext()
  const actionsDisabled = disabled || isStreaming || isStreamOpen || isManualItineraryDeletePending || isManualItineraryMovePending
  const needsChoice = Boolean(item.locationRefinementPrompt?.trim())
  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    eventBus.dispatch({
      type: 'ask_ai_for_item',
      itemId: item.id,
      name: item.name,
      description: item.description,
      locationRefinementPrompt: item.locationRefinementPrompt,
      action: needsChoice ? 'choose_location' : 'add_location',
    })
  }, [eventBus, item.id, item.name, item.description, item.locationRefinementPrompt, needsChoice])

  return <AssistChip size="small" variant="filled" disabled={actionsDisabled} onClick={handleClick} startIcon={needsChoice ? <SolidSparklesIcon /> : <LinePlusIcon />}>
    {needsChoice ? intl.formatMessage({
      id: '26mPbJwH9yGhNIIY8V9T',
      defaultMessage: 'Help me choose',
      description: 'Button that adds an undecided activity to the chat composer context',
    }) : intl.formatMessage({
      id: '0xbLAAewPcIUOXMogdeD',
      defaultMessage: 'Add a location',
      description: 'Button that adds a personal activity to chat context to specify its location',
    })}
  </AssistChip>
}
